import { useCallback, useEffect, useRef, useState } from "react";
import type { MetricStep, PropertyType, ResultType } from "../constants";
import { ALL_PESTS, SPECIALTY_PESTS } from "../constants";
import {
  trackOutcome,
  trackPestSelect,
  trackPropertySelect,
  trackZipCheck,
} from "../lib/analytics";
import { lookupZip } from "../data/zipLookup";
import {
  maxAccessibleStep,
  OUTCOME_PATH,
  pushEstimatorState,
  replaceEstimatorState,
  stepFromPath,
  STEP_PATHS,
  type EstimatorHistoryState,
} from "../lib/estimatorHistory";
import {
  calculateCostRange,
  calculateSeverity,
  getIdentifiedPests,
  hasSpecialtyPest,
} from "../lib/estimator";
import { MetricProgress } from "./MetricProgress";
import { PropertyMetricStep } from "./steps/PropertyMetricStep";
import { ZipMetricStep } from "./steps/ZipMetricStep";
import { DiagnosticQuizStep } from "./steps/DiagnosticQuizStep";
import { DiagnosticReport } from "./report/DiagnosticReport";
import { TerminationScreen } from "./outcomes/TerminationScreen";

interface FormData {
  sqFt: number;
  propertyType: PropertyType;
  zip: string;
  city: string;
  symptoms: string[];
  pestSightings: string[];
}

type FlowState =
  | { phase: "input"; step: MetricStep; data: Partial<FormData> }
  | { phase: "result"; resultType: ResultType; data: FormData };

function flowFromHistoryState(
  state: EstimatorHistoryState,
  data: Partial<FormData>,
): FlowState | null {
  if (state.phase === "input") {
    const step = Math.min(state.step, maxAccessibleStep(data)) as MetricStep;
    return { phase: "input", step, data };
  }
  if (state.phase === "result" && data.sqFt && data.propertyType && data.zip) {
    return {
      phase: "result",
      resultType: state.resultType,
      data: {
        sqFt: data.sqFt,
        propertyType: data.propertyType,
        zip: data.zip,
        city: data.city ?? "",
        symptoms: data.symptoms ?? [],
        pestSightings: data.pestSightings ?? [],
      },
    };
  }
  return null;
}

export function EstimatorWidget() {
  const [flow, setFlow] = useState<FlowState>({ phase: "input", step: 1, data: {} });
  const flowRef = useRef(flow);
  flowRef.current = flow;

  const navigateToInput = useCallback((step: MetricStep, data: Partial<FormData>, replace = false) => {
    const clamped = Math.min(step, maxAccessibleStep(data)) as MetricStep;
    const next: FlowState = { phase: "input", step: clamped, data };
    setFlow(next);
    const state: EstimatorHistoryState = { phase: "input", step: clamped };
    if (replace) {
      replaceEstimatorState(state, STEP_PATHS[clamped]);
    } else {
      pushEstimatorState(state, STEP_PATHS[clamped]);
    }
  }, []);

  const navigateToResult = useCallback((resultType: ResultType, data: FormData) => {
    const next: FlowState = { phase: "result", resultType, data };
    setFlow(next);
    pushEstimatorState({ phase: "result", resultType }, OUTCOME_PATH);
  }, []);

  useEffect(() => {
    const path = window.location.pathname;
    const pathStep = stepFromPath(path);
    const current = flowRef.current;

    if (path === OUTCOME_PATH || path.endsWith(OUTCOME_PATH)) {
      return;
    }

    if (pathStep) {
      const clamped = Math.min(pathStep, maxAccessibleStep(current.data)) as MetricStep;
      if (current.phase === "input" && current.step !== clamped) {
        setFlow({ phase: "input", step: clamped, data: current.data });
      }
      return;
    }

    replaceEstimatorState({ phase: "input", step: 1 }, STEP_PATHS[1]);
  }, []);

  useEffect(() => {
    function onPopState(event: PopStateEvent) {
      const state = event.state as EstimatorHistoryState | null;
      const current = flowRef.current;

      if (state) {
        const next = flowFromHistoryState(state, current.data);
        if (next) {
          setFlow(next);
          return;
        }
      }

      const pathStep = stepFromPath(window.location.pathname);
      if (pathStep && current.phase !== "input") {
        navigateToInput(pathStep, current.data, true);
        return;
      }
      if (pathStep) {
        const clamped = Math.min(pathStep, maxAccessibleStep(current.data)) as MetricStep;
        setFlow({ phase: "input", step: clamped, data: current.data });
      }
    }

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigateToInput]);

  function handlePropertySubmit(sqFt: number, propertyType: PropertyType) {
    trackPropertySelect(propertyType);
    const data = { ...flow.data, sqFt, propertyType };
    navigateToInput(2, data);
  }

  function handleZipSubmit(zip: string) {
    if (flow.phase !== "input") return;
    const city = lookupZip(zip);
    trackZipCheck(zip, !!city);
    if (!city) {
      navigateToResult("invalid_zip", {
        sqFt: flow.data.sqFt ?? 0,
        propertyType: flow.data.propertyType ?? "single-family",
        zip,
        city: "",
        symptoms: [],
        pestSightings: [],
      });
      return;
    }
    navigateToInput(3, { ...flow.data, zip, city });
  }

  function handleDiagnosticSubmit(symptoms: string[], pestSightings: string[]) {
    if (flow.phase !== "input") return;
    const data: FormData = {
      sqFt: flow.data.sqFt!,
      propertyType: flow.data.propertyType!,
      zip: flow.data.zip!,
      city: flow.data.city!,
      symptoms,
      pestSightings,
    };

    if (data.propertyType === "commercial") {
      navigateToResult("commercial", data);
      return;
    }

    navigateToResult("qualified", data);
  }

  useEffect(() => {
    if (flow.phase !== "result") return;
    trackOutcome(flow.resultType === "qualified");
  }, [flow]);

  const showProgress = flow.phase === "input";

  if (flow.phase === "result") {
    const { data, resultType } = flow;

    if (resultType === "commercial" || resultType === "invalid_zip") {
      return (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <TerminationScreen type={resultType} city={data.city || undefined} />
        </div>
      );
    }

    const severity = calculateSeverity(data.symptoms, data.pestSightings);
    const costs = calculateCostRange(data.sqFt, data.zip, data.propertyType);
    const identifiedPests = getIdentifiedPests(data.pestSightings);
    const isSpecialty = hasSpecialtyPest(data.pestSightings);
    const specialtyLabels = data.pestSightings
      .filter(id => SPECIALTY_PESTS.some(p => p.id === id))
      .map(id => SPECIALTY_PESTS.find(p => p.id === id)!.label);

    return (
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <DiagnosticReport
          city={data.city}
          sqFt={data.sqFt}
          costMin={costs.min}
          costMax={costs.max}
          severityLabel={severity.label}
          severityPercent={severity.percent}
          severityLevel={severity.level}
          identifiedPests={identifiedPests}
          showCallButton={!isSpecialty}
          specialtyNotice={
            isSpecialty
              ? `Treatment for ${specialtyLabels.join(", ")} requires a specialty license not supported on the main network. The cost estimate above is for reference only. Find licensed field technicians through specialty channels for this pest category.`
              : undefined
          }
        />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {showProgress && <MetricProgress currentStep={flow.step} />}

      {flow.step === 1 && <PropertyMetricStep onSubmit={handlePropertySubmit} />}
      {flow.step === 2 && (
        <ZipMetricStep onSubmit={handleZipSubmit} onBack={() => history.back()} />
      )}
      {flow.step === 3 && (
        <DiagnosticQuizStep
          onPestSelect={(pestId, isAllowed) => {
            const pest = ALL_PESTS.find(p => p.id === pestId);
            trackPestSelect(pest?.label ?? pestId, isAllowed);
          }}
          onSubmit={handleDiagnosticSubmit}
          onBack={() => history.back()}
        />
      )}
    </div>
  );
}
